import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Clock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { i18n } from "../../../../../i18n/i18n";
import { RootState } from "../../../../../redux/store";
import { formatCurrency } from "../../../../../util";

interface BookingDurationSelectProps {
  value: number;
  onChange: (value: number) => void;
  availableDurations: number[];
  disabled: boolean;
  prices: { [duration: number]: number }; // Add a prices prop to provide the price for each duration
  label: string;
}

const BookingDurationSelect: React.FC<BookingDurationSelectProps> = (props) => {
  const { t } = useTranslation();
  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );

  const formatDuration = (duration: number): string => {
    if (duration < 60) {
      return t("general.minutes_val", { count: duration });
    } else if (duration < 1440) {
      return t("general.hours_val", { count: duration / 60 });
    } else {
      return t("general.days_val", { count: duration / 1440 });
    }
  };

  const handleValueChange = (value: string) => {
    props.onChange(Number(value));
  };

  const hasValue = props.value && props.value !== 0;

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-slate-900">
        {props.label}
        <span className="text-red-500 ml-1">*</span>
      </label>
      <Select
        value={props.value?.toString() || "0"}
        onValueChange={handleValueChange}
        disabled={props.disabled || props.availableDurations.length === 0}
      >
        <SelectTrigger
          className="w-full h-14 text-base border rounded-xl bg-white text-slate-900 border-black"
          aria-label={props.label}
        >
          <div className="flex items-center gap-2 w-full">
            <Clock className="h-5 w-5 text-slate-600 shrink-0" />
            <SelectValue placeholder={props.label}>
              {hasValue ? formatDuration(props.value) : props.label}
            </SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">
            <span className="text-slate-500">{props.label}</span>
          </SelectItem>
          {props.availableDurations.map((duration) => (
            <SelectItem key={duration} value={duration.toString()}>
              <div className="flex items-center justify-between w-full gap-4">
                <span>{formatDuration(duration)}</span>
                {props.prices[duration] > 0 && (
                  <span className="text-slate-600 text-sm">
                    {formatCurrency(props.prices[duration], i18n.language, tenantConfig.currency)}
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default BookingDurationSelect;
